import React from "react"
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  XAxis,
  YAxis
} from "recharts"

import type { AnalyticsActivityTrend } from "../redux/global-map.types"
import { ANALYTICS_ENTITY_COLOR } from "./analytics-constants"

interface ActivityTrendChartProps {
  trend: AnalyticsActivityTrend
}

const SERIES_LABEL: Record<string, string> = {
  lead: "Leads",
  project: "Projects",
  subcontractor: "Subcontractors",
  appointment: "Appointments"
}

const parseBucketDate = (value: string): Date => new Date(`${value}T00:00:00`)

function formatBucketLabel(
  value: string,
  bucketSize: AnalyticsActivityTrend["bucketSize"]
): string {
  const parsed = parseBucketDate(value)
  if (Number.isNaN(parsed.getTime())) return value
  if (bucketSize === "month")
    return parsed.toLocaleDateString(undefined, { month: "short" })
  return parsed.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short"
  })
}

/**
 * Spec §6: qualifying real events (not creation dates alone — see
 * `analytics.py`'s own docstring for the exact source per series), bucketed
 * by day/week/month depending on the selected period's length. Workforce
 * has no event source at all and is correctly absent from `points[].counts`
 * — `unavailableEntities` names it so the legend can say so rather than
 * silently omitting it with no explanation.
 */
export function ActivityTrendChart({ trend }: ActivityTrendChartProps) {
  const seriesKeys = Array.from(
    new Set(trend.points.flatMap((point) => Object.keys(point.counts)))
  )

  const chartData = trend.points.map((point) => ({
    bucket: point.bucket,
    label: formatBucketLabel(point.bucket, trend.bucketSize),
    ...point.counts
  }))

  return (
    <div>
      <ResponsiveContainer width="100%" height={220}>
        <LineChart
          data={chartData}
          margin={{ top: 8, right: 8, left: -16, bottom: 0 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            vertical={false}
            stroke="#f1f5f9"
          />
          <XAxis
            dataKey="label"
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 11, fill: "#64748b" }}
            minTickGap={24}
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 11, fill: "#64748b" }}
          />
          <RechartsTooltip />
          <Legend
            wrapperStyle={{ fontSize: 11 }}
            iconType="circle"
            iconSize={8}
          />
          {seriesKeys.map((key) => (
            <Line
              key={key}
              type="monotone"
              dataKey={key}
              name={SERIES_LABEL[key] ?? key}
              stroke={ANALYTICS_ENTITY_COLOR[key] ?? "#94a3b8"}
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
      {trend.unavailableEntities.length > 0 && (
        <p className="m-0 mt-1 text-[11px] text-slate-400">
          Workforce activity is not tracked yet.
        </p>
      )}
    </div>
  )
}
